/**
 * iter-312: Public ad-hoc vendor submission page.
 *
 * No login required. Vendor lands here via a tokenised link, enters their
 * name/email/phone, fills the same BOQ-style line items, and submits.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import QualityTierBadge from '@/components/QualityTierBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, ChevronDown, ChevronUp, CheckCircle2, LayoutDashboard, Receipt, GripVertical, Paperclip, X as XIcon, FileText } from 'lucide-react';
import TaskProgressSlider from '@/components/TaskProgressSlider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNITS = ['Nos', 'Sqft', 'Sqm', 'Rmt', 'Ltr', 'Kg', 'Lot', 'Day', 'Hour', 'Set', 'Lump'];
const GST_OPTIONS = [0, 5, 12, 18, 28];

function formatINR(n) {
  const v = Math.round(+n || 0);
  return `Rs ${v.toLocaleString('en-IN')}`;
}

export default function PublicVendorSubmit() {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null);

  const blankItem = () => ({
    main_category_id: '', main_category_name: '',
    sub_category_id: '', sub_category_name: '',
    task_title: '', description: '',
    quantity: 1, unit: 'Nos', rate: 0,
    actual_quantity: null, actual_rate: null,
    gst_percentage: 0, discount_percentage: 0,
    notes: '',
    item_type: 'item', is_header: false,
  });
  // iter-313e: ad-hoc header row — vendor can section their submission
  // (e.g. "Material", "Labour", "Site Extras") just like in Edit BOQ.
  const blankHeader = () => ({
    main_category_id: '', main_category_name: '',
    sub_category_id: '', sub_category_name: '',
    task_title: '', description: '',
    quantity: 0, unit: '', rate: 0,
    actual_quantity: null, actual_rate: null,
    gst_percentage: 0, discount_percentage: 0,
    notes: '',
    item_type: 'header', is_header: true,
  });

  const [vendor, setVendor] = useState({ name: '', email: '', phone: '', company: '' });
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  // iter-338i: vendor file attachments (max 2 files, 5MB each, PDF/Image/Excel)
  // Each entry: { file_id, filename, size, content_type, uploading? }
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  // iter-313f: prefill 2 default headers (MATERIAL + LABOUR) each with one blank item
  // so vendors land on a structured template instead of a single empty row.
  const [items, setItems] = useState([
    { ...blankHeader(), task_title: 'MATERIAL' },
    blankItem(),
    { ...blankHeader(), task_title: 'LABOUR' },
    blankItem(),
  ]);
  const [expandedDescs, setExpandedDescs] = useState(new Set());
  const [expandedRemarks, setExpandedRemarks] = useState(new Set());
  const toggleSet = (setter) => (key) => setter(prev => {
    const s = new Set(prev);
    if (s.has(key)) s.delete(key); else s.add(key);
    return s;
  });
  const toggleDesc = toggleSet(setExpandedDescs);
  const toggleRemarks = toggleSet(setExpandedRemarks);

  // iter-312m: localStorage auto-save so vendors don't lose work if their tab closes
  const draftKey = `bcvs:public-draft:${token}`;
  const [draftRestoredAt, setDraftRestoredAt] = useState(null);

  // Restore draft on first load
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d?.vendor) setVendor(d.vendor);
      if (d?.title) setTitle(d.title);
      if (d?.notes) setNotes(d.notes);
      if (Array.isArray(d?.items) && d.items.length) setItems(d.items);
      if (d?.savedAt) setDraftRestoredAt(d.savedAt);
    } catch {/* ignore */}
  }, [token]);

  // Auto-save every 3 seconds when something changes (debounced via timer)
  useEffect(() => {
    if (loading || submitted || error) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({
          vendor, title, notes, items, savedAt: new Date().toISOString(),
        }));
      } catch {/* quota — ignore */}
    }, 3000);
    return () => clearTimeout(t);
  }, [vendor, title, notes, items, draftKey, loading, submitted, error]);

  // Clear draft after successful submit
  useEffect(() => {
    if (submitted) {
      try { localStorage.removeItem(draftKey); } catch {/* ignore */}
    }
  }, [submitted, draftKey]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/vendor-submit/${token}`);
        setLink(r.data);
        const typeLbl = r.data.submission_type === 'bill' ? 'Running Bill'
          : r.data.submission_type[0].toUpperCase() + r.data.submission_type.slice(1);
        // iter-313f: if this link came from Convert-to-Bill, prefill vendor+title+items
        // and don't overwrite from localStorage draft (let the prefill remain authoritative).
        // iter-338f (Msg-680 round-7): If a SERVER-side draft exists for this
        // link, prefer it over the localStorage draft. That way vendors can
        // resume their work across browsers / devices / private windows.
        if (r.data.latest_draft && Array.isArray(r.data.latest_draft.items) && r.data.latest_draft.items.length) {
          const d = r.data.latest_draft;
          if (d.vendor?.name) setVendor((v) => ({ ...v, ...d.vendor }));
          if (d.title) setTitle(d.title);
          if (d.notes) setNotes(d.notes);
          setItems(d.items);
          if (Array.isArray(d.attachments)) setAttachments(d.attachments);
          setDraftRestoredAt(d.saved_at || new Date().toISOString());
          // server-side draft wins; clear stale localStorage
          try { localStorage.removeItem(draftKey); } catch {/* ignore */}
        } else if (r.data.prefill && Array.isArray(r.data.prefill.items)) {
          const pv = r.data.prefill.vendor || {};
          if (pv.name) setVendor((v) => ({ ...v, ...pv }));
          if (r.data.prefill.title) setTitle(r.data.prefill.title);
          setItems(r.data.prefill.items.map((it) => ({
            id: it.id,
            main_category_id: it.main_category_id || '',
            main_category_name: it.main_category_name || '',
            sub_category_id: it.sub_category_id || '',
            sub_category_name: it.sub_category_name || '',
            task_title: it.task_title || '',
            description: it.description || '',
            quantity: it.quantity || 0,
            unit: it.unit || 'Nos',
            rate: it.rate || 0,
            actual_quantity: it.actual_quantity ?? null,
            actual_rate: it.actual_rate ?? null,
            gst_percentage: it.gst_percentage || 0,
            discount_percentage: it.discount_percentage || 0,
            notes: it.notes || '',
            item_type: it.item_type || 'item',
            is_header: !!it.is_header,
            locked: !!it.locked,
            task_progress: it.task_progress || 0,
            task_progress_status: it.task_progress_status || 'not_started',
            task_progress_source: it.task_progress_source,
            task_progress_updated_by: it.task_progress_updated_by,
            task_progress_updated_at: it.task_progress_updated_at,
          })));
          try { localStorage.removeItem(draftKey); } catch {/* ignore */}
        } else {
          setTitle(r.data.project_name ? `${typeLbl} for ${r.data.project_name}` : `${typeLbl} for Bella Casa`);
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Invalid or expired link');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const subsByMain = useMemo(() => {
    if (!link?.sub_categories) return {};
    const m = {};
    for (const s of link.sub_categories) {
      const k = s.main_category_id;
      if (!m[k]) m[k] = [];
      m[k].push(s);
    }
    return m;
  }, [link]);

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const nx = { ...it, [field]: value };
      if (field === 'main_category_id') {
        const cat = (link?.main_categories || []).find(c => c.id === value);
        nx.main_category_name = cat?.name || '';
        nx.sub_category_id = '';
        nx.sub_category_name = '';
      }
      if (field === 'sub_category_id') {
        const subs = subsByMain[nx.main_category_id] || [];
        const sub = subs.find(s => s.id === value);
        nx.sub_category_name = sub?.name || '';
      }
      return nx;
    }));
  };

  // iter-338e (Msg-680 round-5, opt 1b): Append at end of last section + auto-
  // scroll the new row into view + briefly flash it so vendors don't lose
  // track on long quotes.
  const _flashVendorRow = (rowId) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-row-id="${rowId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-amber-400');
        setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 1800);
      }
    }, 80);
  };
  const _appendUnderLastSection = (prev, row) => {
    // Walk from the end; insert AFTER the bottom-most item (so headers stay
    // grouping their items). Effectively the same as append-at-end given
    // sections are linear, but documented for clarity.
    return [...prev, row];
  };
  const addItem = () => {
    const blank = blankItem();
    setItems(prev => _appendUnderLastSection(prev, blank));
    _flashVendorRow(blank.id || blank._editId);
  };
  const addHeader = () => {
    const blank = blankHeader();
    setItems(prev => _appendUnderLastSection(prev, blank));
    _flashVendorRow(blank.id || blank._editId);
  };
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // iter-321: Vendor Task Progress (Running Bill flavour only) — POST to the
  // public progress endpoint and mirror the response onto the local row.
  const handleVendorProgressUpdate = async (idx, itemId, next) => {
    try {
      const res = await axios.post(`${API}/public/vendor-submit/${token}/item-progress`, {
        item_id: itemId,
        vendor_name: vendor?.name || '',
        ...next,
      });
      const d = res.data || {};
      setItems(prev => prev.map((it, i) => i === idx ? {
        ...it,
        task_progress: d.task_progress ?? it.task_progress,
        task_progress_status: d.task_progress_status ?? it.task_progress_status,
        task_progress_source: d.task_progress_source || 'vendor',
        task_progress_updated_by: d.task_progress_updated_by,
        task_progress_updated_at: d.task_progress_updated_at,
      } : it));
      toast.success('Progress saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save progress');
    }
  };

  // iter-315b: drag-reorder for headers & items on the Public Vendor Submit page.
  // Vendors often realise mid-fill that a row belongs under a different section —
  // letting them drag avoids manual re-typing. Locked (Running Bill) rows can't move.
  const [dragRowIdx, setDragRowIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const handleDragStart = (idx) => (e) => {
    setDragRowIdx(idx);
    try { e.dataTransfer.effectAllowed = 'move'; } catch {}
  };
  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragRowIdx === null || dragRowIdx === idx) return;
    setDragOverIdx(idx);
    try { e.dataTransfer.dropEffect = 'move'; } catch {}
  };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragRowIdx === null || dragRowIdx === idx) {
      setDragRowIdx(null);
      setDragOverIdx(null);
      return;
    }
    setItems(prev => {
      const arr = [...prev];
      // Prevent moving locked rows or dropping onto a locked target.
      if (arr[dragRowIdx]?.locked || arr[idx]?.locked) return prev;
      const [moved] = arr.splice(dragRowIdx, 1);
      arr.splice(idx, 0, moved);
      return arr;
    });
    setDragRowIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => {
    setDragRowIdx(null);
    setDragOverIdx(null);
  };

  // iter-338i: Upload a single attachment to GridFS via the public endpoint.
  // Validates type/size client-side, then POSTs multipart/form-data.
  const ALLOWED_ATTACHMENT_TYPES = useMemo(() => [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ], []);
  const handleAttachmentChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (attachments.length + files.length > 2) {
      toast.error('Maximum 2 attachments allowed');
      return;
    }
    setUploadingAttachment(true);
    for (const file of files) {
      const isImage = (file.type || '').startsWith('image/');
      if (!isImage && !ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
        toast.error(`Unsupported type: ${file.name}. Allowed: PDF, Image, Excel.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5 MB`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/public/vendor-submit/${token}/upload-attachment`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setAttachments(prev => prev.length >= 2 ? prev : [...prev, r.data]);
        toast.success(`${r.data.filename} attached`);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setUploadingAttachment(false);
  };
  const removeAttachment = (file_id) => {
    setAttachments(prev => prev.filter(a => a.file_id !== file_id));
  };

  const totals = useMemo(() => {
    let s = 0, g = 0, d = 0, aS = 0, aG = 0, aD = 0;
    for (const it of items) {
      // iter-313e: section-header rows have no qty/rate — skip from totals
      if (it.item_type === 'header' || it.is_header) continue;
      const q = +it.quantity || 0, r = +it.rate || 0;
      const b = q * r;
      const di = b * (+it.discount_percentage || 0) / 100;
      const gi = (b - di) * (+it.gst_percentage || 0) / 100;
      s += b; d += di; g += gi;
      const aq = it.actual_quantity != null ? +it.actual_quantity : q;
      const ar = it.actual_rate != null ? +it.actual_rate : r;
      const ab = aq * ar;
      const aDi = ab * (+it.discount_percentage || 0) / 100;
      const aGi = (ab - aDi) * (+it.gst_percentage || 0) / 100;
      aS += ab; aD += aDi; aG += aGi;
    }
    return { est: s - d + g, act: aS - aD + aG, gst: g, aGst: aG };
  }, [items]);

  // iter-338e (Msg-680 round-5, Bug 2): The previous filter silently DROPPED
  // any item that didn't have `main_category_id` set, even if the vendor had
  // filled the task_title — so a quote with 10 items but only 3 categorised
  // would submit with only 3 items, the rest vanishing. We now:
  //   • Keep all rows that have a non-empty task_title (categories optional)
  //   • Block submit only when ZERO items have a title
  //   • Surface an explicit warning when items are missing categories so the
  //     vendor can fix them, but we never silently drop typed content.
  const _cleanForSubmit = () => items.filter(it => {
    const isHeader = it.item_type === 'header' || it.is_header;
    return (it.task_title || '').trim().length > 0 || (isHeader && (it.task_title || '').trim());
  });

  const handleSubmit = async (mode = 'submitted') => {
    if (!vendor.name.trim() || !vendor.email.trim()) {
      toast.error('Your Name and Email are required');
      return;
    }
    if (!title.trim()) { toast.error('Title is required'); return; }
    const cleanItems = _cleanForSubmit();
    const realItemCount = cleanItems.filter(it => !(it.item_type === 'header' || it.is_header)).length;
    if (realItemCount === 0) {
      toast.error('Add at least one line item with a title');
      return;
    }
    // Surface (not silently drop) any items missing Main Category.
    const missingCat = cleanItems.filter(it =>
      !(it.item_type === 'header' || it.is_header) && !it.main_category_id
    );
    if (missingCat.length > 0 && mode === 'submitted') {
      const proceed = window.confirm(
        `${missingCat.length} item(s) don\u2019t have a Main Category yet.\n\nYou can still SAVE these now and complete the category later, or pick one before SENDING.\n\nClick OK to send anyway, Cancel to fix them first.`
      );
      if (!proceed) return;
    }
    setSubmitting(true);
    try {
      // iter-338f: also force-save the localStorage draft right now so even
      // if the API call hiccups, the vendor's browser still has the latest
      // state to resume from on next mount.
      try {
        localStorage.setItem(draftKey, JSON.stringify({
          vendor, title, notes, items: cleanItems, savedAt: new Date().toISOString(),
        }));
      } catch {/* quota — ignore */}
      const r = await axios.post(`${API}/public/vendor-submit/${token}`, {
        vendor, title, items: cleanItems, notes, status: mode,
        attachments,
      });
      setSubmittedResult(r.data);
      if (mode === 'draft') {
        toast.success(`Draft saved (${cleanItems.length} line item(s)). You can close this tab and reopen the link to continue.`, { duration: 6000 });
        setDraftRestoredAt(new Date().toISOString());
      } else {
        setSubmitted(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };
  const handleSaveDraft = () => handleSubmit('draft');
  const handleSendForApproval = () => handleSubmit('submitted');

  // iter-312l: vendor can email themselves a styled preview before submitting
  const [previewing, setPreviewing] = useState(false);
  const handleEmailPreview = async () => {
    if (!vendor.name.trim() || !vendor.email.trim()) {
      toast.error('Please enter your Name and Email first — the preview is sent to your email.');
      return;
    }
    if (!title.trim()) { toast.error('Title is required'); return; }
    const cleanItems = items.filter(it => {
      const isHeader = it.item_type === 'header' || it.is_header;
      if (isHeader) return (it.task_title || '').trim();
      return (it.task_title || '').trim() && it.main_category_id;
    });
    const realItemCount = cleanItems.filter(it => !(it.item_type === 'header' || it.is_header)).length;
    if (realItemCount === 0) {
      toast.error('Add at least one line item with Title + Main Category before previewing');
      return;
    }
    setPreviewing(true);
    try {
      const r = await axios.post(`${API}/public/vendor-submit/${token}/email-preview`, {
        vendor, title, items: cleanItems, notes,
      });
      toast.success(`Preview emailed to ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCancel = () => {
    if (!window.confirm('Discard this submission and close the form?')) return;
    // Soft-cancel: navigate to a friendly confirmation card
    window.location.href = 'about:blank';
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-lg font-bold text-stone-900 mb-2">Link Not Available</h2>
            <p className="text-sm text-stone-600">{error}</p>
            <p className="text-xs text-stone-400 mt-4">Please contact the Bella Casa team to request a fresh link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-stone-900 mb-2">Submitted Successfully</h2>
            <p className="text-sm text-stone-600">
              Thank you, {vendor.name}. We have received your submission ({formatINR(submittedResult?.grand_total)}). The Bella Casa team will review and get back to you on <strong>{vendor.email}</strong>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const typeLbl = link.submission_type === 'bill' ? 'Running Bill'
    : link.submission_type[0].toUpperCase() + link.submission_type.slice(1);

  return (
    // iter-313e: page now renders at default 80% (inherits global `html { zoom: 0.8 }`).
    // Vendors view this on mobile — keeping it compact maximises visible content.
    <div className="min-h-screen bg-stone-50 py-4 px-2 sm:px-6">
      <Toaster richColors position="top-right" />
      <div className="w-full">
        {/* Header */}
        <div className="bg-white border border-stone-200 rounded-lg p-5 mb-4 shadow-sm">
          <h1 className="text-xl sm:text-2xl font-bold text-stone-900">Submit your {typeLbl}</h1>
          <p className="text-sm text-stone-500 mt-1">
            {link.project_name ? <>For project <strong>{link.project_name}</strong> · </> : null}
            {link.company_name || 'Bella Casa Interiors'}
          </p>
          <p className="text-xs text-stone-400 mt-2">
            No account needed. Fill the form below and click Submit. We'll email you at the address you provide.
          </p>
          {/* iter-312m: clear how-to-fill guidance, matching the BOQ Edit flow */}
          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900 leading-relaxed">
            <strong className="text-blue-800">How to fill:</strong>{' '}
            Use the <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded font-semibold">Add Header</span> button to insert a section banner (e.g. <em>Material, Labour, Site Extras</em>) for grouping related rows. Then enter the <strong>Item Title</strong>, pick <strong>Category</strong> &amp; <strong>Sub-category</strong>, add an optional <strong>Description</strong>, then under <span className="px-1.5 py-0.5 bg-blue-100 rounded font-semibold">Estimated (Design)</span> fill
            <em> Qty, Unit, Rate</em>{' '}— this is your initial proposal. Use <span className="px-1.5 py-0.5 bg-green-100 text-green-900 rounded font-semibold">Actual (Site)</span> to
            record the final on-ground figures during or after the work. Add <strong>GST%</strong> and any <strong>Remarks</strong>, then click
            <span className="px-1.5 py-0.5 mx-1 bg-blue-600 text-white rounded font-semibold">Save &amp; Send for Approval</span> when done.
          </div>
          {draftRestoredAt && (
            <div className="mt-2 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="draft-restored-banner">
              <span><strong>Draft restored</strong> from {new Date(draftRestoredAt).toLocaleString('en-IN')} — keep editing where you left off. We auto-save your progress in this browser.</span>
              <button
                type="button"
                onClick={() => { try { localStorage.removeItem(draftKey); } catch {} window.location.reload(); }}
                className="text-amber-700 hover:text-amber-900 underline ml-2"
                data-testid="discard-draft-btn"
              >Discard draft</button>
            </div>
          )}
        </div>

        {/* iter-313f: Running Bill mode banner — surfaces when items were prefilled
            from an approved Quote so the vendor immediately understands which
            fields are locked and where to add additional work. */}
        {link.prefill && (
          <div className="mb-4 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 sm:p-4" data-testid="running-bill-banner">
            <div className="flex items-start gap-2">
              <Receipt className="text-emerald-700 mt-0.5 shrink-0" size={18} />
              <div className="text-xs sm:text-sm text-emerald-900 leading-relaxed">
                <strong className="text-emerald-800 uppercase tracking-wide text-[11px] sm:text-xs block mb-1">Running Bill — Site Billing Mode</strong>
                The original Quote items below are <strong>locked</strong>. You can only update the
                <span className="px-1.5 py-0.5 bg-green-200 text-green-900 rounded font-semibold mx-1">Actual (Site)</span> Qty/Rate, Description and Remarks for those rows.
                For any <em>additional work</em> done on site, click <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded font-semibold">Add Header</span> below to start a new section (e.g. "ADDITIONAL WORK") and add fresh line items under it.
              </div>
            </div>
          </div>
        )}

        {/* Vendor Info */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-stone-700 mb-3">Your Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={vendor.name} onChange={e => setVendor({ ...vendor, name: e.target.value })} placeholder="Your full name" data-testid="public-vendor-name" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={vendor.email} onChange={e => setVendor({ ...vendor, email: e.target.value })} placeholder="you@company.com" data-testid="public-vendor-email" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={vendor.phone} onChange={e => setVendor({ ...vendor, phone: e.target.value })} placeholder="+91 …" data-testid="public-vendor-phone" />
              </div>
              <div>
                <Label className="text-xs">Company</Label>
                <Input value={vendor.company} onChange={e => setVendor({ ...vendor, company: e.target.value })} placeholder="Optional" data-testid="public-vendor-company" />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Submission Title *</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="public-title" />
            </div>
          </CardContent>
        </Card>

        {/* Items */}
        <Card>
          <CardContent className="p-0">
            <div className="bg-stone-50 px-3 py-2 flex items-center justify-between gap-2 border-b border-stone-200 flex-wrap">
              <span className="text-sm font-semibold text-stone-700">Line Items ({items.filter(i => !(i.item_type === 'header' || i.is_header)).length})</span>
              <div className="flex items-center gap-2 flex-wrap">
                <Button type="button" size="sm" variant="outline" onClick={addHeader} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="public-add-header">
                  <LayoutDashboard size={12} className="mr-1" />Add Header
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="public-add-item">
                  <Plus size={12} className="mr-1" />Add Item
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: 44 }} />
                  <col style={{ width: 380 }} />
                  <col style={{ width: 76 }} />
                  <col style={{ width: 70 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 76 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 70 }} />
                  <col style={{ width: 180 }} />
                  <col style={{ width: 40 }} />
                </colgroup>
                <thead className="bg-stone-50 text-stone-600">
                  <tr className="border-b-2 border-stone-200">
                    <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">SR</th>
                    <th rowSpan={2} className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">Description</th>
                    <th colSpan={4} className="px-2 py-2 text-center text-[11px] font-bold bg-blue-50 text-blue-800 border-r border-stone-200 border-b border-blue-100 uppercase tracking-wide">Estimated (Design)</th>
                    <th colSpan={3} className="px-2 py-2 text-center text-[11px] font-bold bg-green-50 text-green-800 border-r border-stone-200 border-b border-green-100 uppercase tracking-wide">Actual (Site)</th>
                    <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200 bg-amber-50 text-amber-700">Variance</th>
                    <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">GST%</th>
                    <th rowSpan={2} className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">Remarks</th>
                    <th rowSpan={2} className="px-2 py-2 text-center text-[11px]"><Trash2 size={11} /></th>
                  </tr>
                  <tr>
                    <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Qty</th>
                    <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Unit</th>
                    <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Rate</th>
                    <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 border-r border-stone-200 uppercase tracking-wide">Amt</th>
                    <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 uppercase tracking-wide">Qty</th>
                    <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 uppercase tracking-wide">Rate</th>
                    <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 border-r border-stone-200 uppercase tracking-wide">Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    // iter-313e: render section headers as amber banners spanning the entire table
                    if (it.item_type === 'header' || it.is_header) {
                      const isDragOver = dragOverIdx === idx;
                      return (
                        <tr
                          key={idx}
                          className={`bg-amber-100 border-y-2 border-amber-400 ${isDragOver ? 'outline outline-2 outline-blue-500' : ''}`}
                          data-testid={`public-header-row-${idx}`}
                          draggable={!it.locked}
                          onDragStart={handleDragStart(idx)}
                          onDragOver={handleDragOver(idx)}
                          onDrop={handleDrop(idx)}
                          onDragEnd={handleDragEnd}
                        >
                          <td className="px-2 py-2 text-center text-amber-800 font-bold">
                            <div className="flex items-center justify-center gap-1">
                              {!it.locked && (
                                <GripVertical size={12} className="text-amber-700/70 cursor-grab active:cursor-grabbing" title="Drag to reorder" />
                              )}
                              <span>{idx + 1}</span>
                            </div>
                          </td>
                          <td colSpan={11} className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <LayoutDashboard size={14} className="text-amber-800 shrink-0" />
                              <input
                                value={it.task_title}
                                onChange={e => updateItem(idx, 'task_title', e.target.value)}
                                placeholder="Section title (e.g. Material, Labour, Site Extras)"
                                className="flex-1 bg-transparent border-0 outline-none text-sm font-bold text-amber-900 uppercase tracking-wide placeholder:text-amber-400 placeholder:font-normal placeholder:normal-case"
                                data-testid={`public-header-${idx}-title`}
                              />
                            </div>
                          </td>
                          <td className="p-1 text-center">
                            <button type="button" onClick={() => removeItem(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Remove header">
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    }
                    const eq = +it.quantity || 0;
                    const er = +it.rate || 0;
                    const estAmt = eq * er;
                    const aq = it.actual_quantity != null ? +it.actual_quantity : eq;
                    const ar = it.actual_rate != null ? +it.actual_rate : er;
                    const actAmt = aq * ar;
                    const variance = actAmt - estAmt;
                    const subs = subsByMain[it.main_category_id] || [];
                    const descKey = `d_${idx}`;
                    const remKey = `r_${idx}`;
                    const isDescExpanded = expandedDescs.has(descKey);
                    const isRemExpanded = expandedRemarks.has(remKey);
                    // iter-313f: when items are cloned from an approved Quote into a
                    // Running Bill, the original lines are locked — vendor may only
                    // edit Actual (Site) Qty/Rate, Description, and Remarks.
                    const isLocked = !!it.locked;
                    const isDragOver = dragOverIdx === idx;
                    return (
                      <tr
                        key={idx}
                        className={`border-b border-stone-200 align-top ${isLocked ? 'bg-stone-50/80' : 'hover:bg-stone-50/60'} ${isDragOver ? 'outline outline-2 outline-blue-500' : ''}`}
                        data-testid={`public-item-row-${idx}`}
                        draggable={!isLocked}
                        onDragStart={handleDragStart(idx)}
                        onDragOver={handleDragOver(idx)}
                        onDrop={handleDrop(idx)}
                        onDragEnd={handleDragEnd}
                      >
                        <td className="px-2 py-3 text-center text-stone-500">
                          <div className="flex items-center justify-center gap-1">
                            {!isLocked && (
                              <GripVertical size={12} className="text-stone-400 cursor-grab active:cursor-grabbing" title="Drag to reorder" />
                            )}
                            <span>{idx + 1}</span>
                          </div>
                          {isLocked && (
                            <div className="text-[9px] uppercase tracking-wide text-stone-500 font-semibold mt-0.5" title="From approved Quote — locked">Quote</div>
                          )}
                        </td>
                        <td className="px-2 py-2 border-r border-stone-100">
                          <div className="space-y-1.5">
                            <div className="flex items-start gap-1.5">
                              {/* iter-312n: wrap long titles by switching to an auto-growing textarea */}
                              <textarea
                                value={it.task_title}
                                onChange={e => updateItem(idx, 'task_title', e.target.value)}
                                onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                                rows={1}
                                placeholder="Item title"
                                className={`flex-1 text-sm font-semibold text-stone-900 border-0 bg-transparent px-1 py-1 outline-none resize-none whitespace-pre-wrap break-words leading-snug rounded ${isLocked ? 'text-stone-700 cursor-not-allowed' : 'focus:bg-white focus:ring-1 focus:ring-blue-200'}`}
                                required
                                readOnly={isLocked}
                                data-testid={`public-item-${idx}-task-title`}
                              />
                              {/* iter-386u-b — Quality Tier tag (public vendor submission view) */}
                              {it.quality_tier && it.quality_tier !== 'standard' && (
                                <span className="flex-shrink-0 mt-1.5"><QualityTierBadge tier={it.quality_tier} size="xs" /></span>
                              )}
                            </div>
                            {/* iter-312n: stack Category & Sub-Category vertically so both labels stay fully visible on narrow column */}
                            <div className={`flex flex-col gap-1.5 rounded-md px-1.5 py-1.5 border ${isLocked ? 'bg-stone-100 border-stone-200' : 'bg-stone-100 border-stone-300'}`}>
                          <select value={it.main_category_id || ''} onChange={e => updateItem(idx, 'main_category_id', e.target.value)} disabled={isLocked} className={`w-full h-7 text-xs bg-white border border-stone-200 rounded px-1 outline-none text-stone-700 ${isLocked ? 'cursor-not-allowed opacity-90' : ''}`} required>
                                <option value="">+ Category</option>
                                {(link.main_categories || []).map(c => <option key={c.id} value={c.id}>{c.code ? `${c.code} — ` : ''}{c.name}</option>)}
                              </select>
                              {it.main_category_id && subs.length > 0 && (
                                <select value={it.sub_category_id || ''} onChange={e => updateItem(idx, 'sub_category_id', e.target.value)} disabled={isLocked} className={`w-full h-7 text-xs bg-white border border-stone-200 rounded px-1 outline-none text-stone-700 ${isLocked ? 'cursor-not-allowed opacity-90' : ''}`}>
                                  <option value="">+ Sub-category</option>
                                  {subs.map(s => <option key={s.id} value={s.id}>{s.code ? `${s.code} — ` : ''}{s.name}</option>)}
                                </select>
                              )}
                            </div>
                            {isDescExpanded ? (
                              <div className="relative">
                                <Textarea value={it.description || ''} onChange={e => updateItem(idx, 'description', e.target.value)} className="text-xs text-stone-600 border border-blue-200 bg-blue-50/30 rounded resize-y" style={{ minHeight: '60px' }} placeholder="Description / scope detail" />
                                <button type="button" onClick={() => toggleDesc(descKey)} className="absolute top-1 right-1 p-0.5 bg-stone-200/80 hover:bg-stone-300 rounded text-stone-500"><ChevronUp size={12} /></button>
                              </div>
                            ) : (
                              <div className="relative cursor-pointer" onClick={() => toggleDesc(descKey)}>
                                <div className="text-xs text-stone-500 leading-relaxed line-clamp-2 pr-4">
                                  {it.description || <span className="text-stone-300 italic">Click to add description</span>}
                                </div>
                                {(it.description || '').length > 60 && <ChevronDown size={12} className="absolute right-0 top-1 text-stone-400" />}
                              </div>
                            )}
                            {/* iter-321: Vendor task progress (running-bill only) */}
                            {link?.submission_type === 'bill' && it.id && (
                              <div className="mt-1.5 pt-1.5 border-t border-stone-100">
                                <TaskProgressSlider
                                  itemId={it.id}
                                  progress={it.task_progress || 0}
                                  status={it.task_progress_status || 'not_started'}
                                  source={it.task_progress_source}
                                  updatedBy={it.task_progress_updated_by}
                                  updatedAt={it.task_progress_updated_at}
                                  compact
                                  onChange={(next) => handleVendorProgressUpdate(idx, it.id, next)}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-1 py-1 bg-blue-50/20 border-l border-blue-100">
                          <Input type="number" min="0" step="0.01" value={eq} readOnly={isLocked} disabled={isLocked} onChange={e => updateItem(idx, 'quantity', e.target.value === '' ? 0 : +e.target.value)} className={`h-9 w-full text-center text-sm text-blue-700 border-0 bg-transparent px-1 ${isLocked ? 'cursor-not-allowed' : ''}`} />
                        </td>
                        <td className="px-1 py-1 bg-blue-50/20">
                          <select value={it.unit} disabled={isLocked} onChange={e => updateItem(idx, 'unit', e.target.value)} className={`h-9 w-full text-xs text-stone-600 border-0 bg-transparent ${isLocked ? 'cursor-not-allowed' : ''}`}>
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </td>
                        <td className="px-1 py-1 bg-blue-50/20">
                          <Input type="number" min="0" step="0.01" value={er} readOnly={isLocked} disabled={isLocked} onChange={e => updateItem(idx, 'rate', e.target.value === '' ? 0 : +e.target.value)} className={`h-9 w-full text-right text-sm text-stone-700 border-0 bg-transparent px-1 ${isLocked ? 'cursor-not-allowed' : ''}`} />
                        </td>
                        <td className="px-2 py-3 text-right font-semibold text-blue-700 bg-blue-50/30 text-sm whitespace-nowrap border-r border-stone-100">{formatINR(estAmt)}</td>
                        <td className="px-1 py-1 bg-green-50/20 border-l border-green-100">
                          <Input type="number" min="0" step="0.01" value={it.actual_quantity ?? ''} onChange={e => updateItem(idx, 'actual_quantity', e.target.value === '' ? null : +e.target.value)} placeholder={`${eq}`} className="h-9 w-full text-center text-sm text-green-700 border-0 bg-transparent px-1" data-testid={`public-item-${idx}-actual-qty`} />
                        </td>
                        <td className="px-1 py-1 bg-green-50/20">
                          <Input type="number" min="0" step="0.01" value={it.actual_rate ?? ''} onChange={e => updateItem(idx, 'actual_rate', e.target.value === '' ? null : +e.target.value)} placeholder={`${er}`} className="h-9 w-full text-right text-sm text-stone-700 border-0 bg-transparent px-1" data-testid={`public-item-${idx}-actual-rate`} />
                        </td>
                        <td className="px-2 py-3 text-right font-semibold text-green-700 bg-green-50/30 text-sm whitespace-nowrap border-r border-stone-100">{formatINR(actAmt)}</td>
                        <td className={`px-2 py-3 text-right text-sm font-medium whitespace-nowrap border-r border-stone-100 ${variance > 0 ? 'text-red-600 bg-red-50/30' : variance < 0 ? 'text-green-600 bg-green-50/20' : 'text-stone-400'}`}>
                          {variance !== 0 ? (variance > 0 ? '+' : '') + formatINR(variance) : '-'}
                        </td>
                        <td className="px-0 py-1 border-r border-stone-100">
                          <select value={it.gst_percentage} disabled={isLocked} onChange={e => updateItem(idx, 'gst_percentage', +e.target.value)} className={`h-9 w-full text-center text-sm text-stone-900 border-0 bg-transparent ${isLocked ? 'cursor-not-allowed' : ''}`}>
                            {GST_OPTIONS.map(g => <option key={g} value={g}>{g}%</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2 border-r border-stone-100">
                          {isRemExpanded ? (
                            <div className="relative">
                              <Textarea value={it.notes || ''} onChange={e => updateItem(idx, 'notes', e.target.value)} className="text-xs text-stone-600 border border-blue-200 bg-blue-50/30 rounded resize-y" style={{ minHeight: '60px' }} placeholder="Add remarks..." />
                              <button type="button" onClick={() => toggleRemarks(remKey)} className="absolute top-1 right-1 p-0.5 bg-stone-200/80 hover:bg-stone-300 rounded text-stone-500"><ChevronUp size={12} /></button>
                            </div>
                          ) : (
                            <div className="relative cursor-pointer" onClick={() => toggleRemarks(remKey)}>
                              <div className="text-xs text-stone-600 leading-relaxed line-clamp-3 pr-4">
                                {it.notes || <span className="text-stone-300 italic">Click to add remarks</span>}
                              </div>
                              {(it.notes || '').length > 60 && <ChevronDown size={12} className="absolute right-0 top-1 text-stone-400" />}
                            </div>
                          )}
                        </td>
                        <td className="p-1 text-center">
                          {items.length > 1 && !isLocked && (
                            <button type="button" onClick={() => removeItem(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Remove">
                              <Trash2 size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* iter-338f (Msg-680 round-7): mirror "Add Header / Add Item"
               buttons at the BOTTOM of the table so vendors with long quotes
               don't have to scroll back to the top to add another row. */}
            <div className="bg-stone-50 px-3 py-2 flex items-center justify-end gap-2 border-t border-stone-200 flex-wrap">
              <Button type="button" size="sm" variant="outline" onClick={addHeader} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="public-add-header-bottom">
                <LayoutDashboard size={12} className="mr-1" />Add Header
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="public-add-item-bottom">
                <Plus size={12} className="mr-1" />Add Item
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="mt-4">
          <CardContent className="p-4">
            <Label className="text-xs">Overall Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional context..." data-testid="public-notes" />
          </CardContent>
        </Card>

        {/* iter-338i: Attachments — up to 2 files (PDF/Image/Excel, 5 MB each) */}
        <Card className="mt-4" data-testid="public-attachments-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  <Paperclip size={12} />Attachments (optional)
                </Label>
                <p className="text-[11px] text-stone-500 mt-0.5">Up to 2 files · PDF, Image, or Excel · max 5 MB each</p>
              </div>
              {attachments.length < 2 && (
                <label
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 cursor-pointer"
                  data-testid="public-add-attachment-btn"
                >
                  <Plus size={12} />
                  {uploadingAttachment ? 'Uploading…' : 'Add file'}
                  <input
                    type="file"
                    multiple
                    onChange={handleAttachmentChange}
                    disabled={uploadingAttachment}
                    accept=".pdf,.xls,.xlsx,.csv,image/*,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    data-testid="public-attachment-input"
                  />
                </label>
              )}
            </div>
            {attachments.length === 0 ? (
              <p className="text-xs text-stone-400 italic mt-2">No files attached yet — add a quotation PDF, photos, or an Excel sheet here.</p>
            ) : (
              <ul className="mt-2 space-y-1.5" data-testid="public-attachments-list">
                {attachments.map((att, i) => (
                  <li
                    key={att.file_id}
                    className="flex items-center justify-between gap-2 text-xs bg-stone-50 border border-stone-200 rounded px-2 py-1.5"
                    data-testid={`public-attachment-${i}`}
                  >
                    <span className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText size={14} className="text-blue-600 shrink-0" />
                      <span className="truncate font-medium text-stone-700">{att.filename}</span>
                      <span className="text-stone-400 shrink-0">({Math.round((att.size || 0) / 1024)} KB)</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(att.file_id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded shrink-0"
                      title="Remove"
                      data-testid={`public-remove-attachment-${i}`}
                    >
                      <XIcon size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Totals + Footer Buttons */}
        <div className="sticky bottom-0 bg-white border-t border-stone-200 mt-4 -mx-3 sm:-mx-6 p-4 shadow-lg">
          <div className="max-w-[1500px] mx-auto flex items-center justify-between flex-wrap gap-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><span className="text-stone-500 text-xs block">Est Total</span><span className="font-semibold">{formatINR(totals.est)}</span></div>
              <div><span className="text-stone-500 text-xs block">Actual Total</span><span className="font-semibold text-green-800">{formatINR(totals.act)}</span></div>
              <div><span className="text-stone-500 text-xs block">Variance</span><span className={`font-semibold ${totals.act - totals.est > 0 ? 'text-red-700' : totals.act - totals.est < 0 ? 'text-green-700' : 'text-stone-500'}`}>{totals.act - totals.est > 0 ? '+' : ''}{formatINR(totals.act - totals.est)}</span></div>
              <div><span className="text-stone-500 text-xs block">GST (Est / Act)</span><span className="font-semibold text-xs">{formatINR(totals.gst)} / {formatINR(totals.aGst)}</span></div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button type="button" variant="outline" onClick={handleCancel} disabled={submitting || previewing} data-testid="public-cancel-btn">
                Cancel
              </Button>
              <Button type="button" variant="outline" onClick={handleEmailPreview} disabled={submitting || previewing} data-testid="public-email-preview-btn">
                {previewing ? 'Sending…' : 'Email Preview'}
              </Button>
              {/* iter-338e (Msg-680 round-5): split Save vs Send. Save Draft
                 persists the quote in `status='draft'` so the vendor can
                 reopen the link later without re-typing; Send submits for
                 admin approval as before. */}
              <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={submitting || previewing} className="border-stone-400 text-stone-700 hover:bg-stone-100" data-testid="public-save-draft-btn">
                {submitting ? 'Saving…' : 'Save Draft'}
              </Button>
              <Button type="button" onClick={handleSendForApproval} disabled={submitting || previewing} className="bg-blue-600 hover:bg-blue-700 text-white px-6" data-testid="public-submit-btn">
                {submitting ? 'Sending…' : 'Send for Approval'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
